import React, { createContext, useState, useCallback, useEffect } from 'react';
import { authAPI } from '../services/api';
import { CompanySettings, User } from '../types';
import { resolvePermissionModule } from '../constants/permissions';

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (data: Record<string, unknown>) => Promise<void>;
  logout: () => void;
  loading: boolean;
  authReady: boolean;
  refreshUser: () => Promise<void>;
  applyUser: (next: User) => void;
  patchCompany: (patch: Partial<CompanySettings>) => void;
  hasPermission: (module: string, action?: string) => boolean;
  isSuperAdmin: boolean;
  isTenantAdmin: boolean;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

const readStoredAuth = () => {
  try {
    const savedToken = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    return {
      token: savedToken,
      user: savedUser ? (JSON.parse(savedUser) as User) : null,
      loading: Boolean(savedToken),
    };
  } catch {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    return { token: null, user: null, loading: false };
  }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const stored = readStoredAuth();
  const [user, setUser] = useState<User | null>(stored.user);
  const [token, setToken] = useState<string | null>(stored.token);
  const [loading, setLoading] = useState(stored.loading);
  const [authReady, setAuthReady] = useState(!stored.loading);

  const refreshUser = useCallback(async () => {
    const savedToken = localStorage.getItem('token');
    if (!savedToken) {
      setLoading(false);
      setAuthReady(true);
      return;
    }

    setToken(savedToken);
    try {
      const response = await authAPI.getCurrentUser();
      const userData = response.data;
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setToken(null);
        setUser(null);
      }
    } finally {
      setLoading(false);
      setAuthReady(true);
    }
  }, []);

  useEffect(() => {
    if (stored.token) refreshUser();
    else setAuthReady(true);
  }, [refreshUser]); // eslint-disable-line react-hooks/exhaustive-deps

  const login = useCallback(async (email: string, password: string) => {
    setLoading(true);
    try {
      const response = await authAPI.login(email, password);
      const { token: newToken, user: userData } = response.data;
      localStorage.setItem('token', newToken);
      localStorage.setItem('user', JSON.stringify(userData));
      setToken(newToken);
      setUser(userData);
      setAuthReady(true);
    } finally {
      setLoading(false);
    }
  }, []);

  const register = useCallback(async (data: Record<string, unknown>) => {
    setLoading(true);
    try {
      await authAPI.register(data);
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
    setAuthReady(true);
  }, []);

  const patchCompany = useCallback((patch: Partial<CompanySettings>) => {
    setUser((prev) => {
      if (!prev?.company) return prev;
      const next = { ...prev, company: { ...prev.company, ...patch } };
      localStorage.setItem('user', JSON.stringify(next));
      return next;
    });
  }, []);

  const applyUser = useCallback((next: User) => {
    setUser(next);
    localStorage.setItem('user', JSON.stringify(next));
  }, []);

  const hasPermission = useCallback(
    (module: string, action = 'view') => {
      if (!user) return false;
      if (user.roleType === 'super_admin' || user.roleType === 'tenant_admin') return true;
      const permModule = resolvePermissionModule(module);
      return Boolean(user.permissions?.[permModule]?.[action as keyof typeof user.permissions[string]]);
    },
    [user]
  );

  const isSuperAdmin = user?.roleType === 'super_admin';
  const isTenantAdmin = user?.roleType === 'tenant_admin';

  return (
    <AuthContext.Provider
      value={{ user, token, login, register, logout, loading, authReady, refreshUser, applyUser, patchCompany, hasPermission, isSuperAdmin, isTenantAdmin }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
